/**
 * Signals ("warm lead radar") — mirrors ld-backend constants/signals.js and the
 * /signals API. The ld-gcp-signal-collector Cloud Function reads LinkedIn; the app
 * manages sources and decides on the people found.
 */

export type SignalSourceType = 'profile_views' | 'job_changes' | 'own_posts' | 'post';
export type SignalType = 'profile_view' | 'post_comment' | 'post_reaction' | 'job_change';
export type SignalLeadStatus = 'new' | 'approved' | 'dismissed' | 'in_campaign';
/** 'open' = new + approved (low fits left out); 'low_fit' = new, scored under the ignore score; 'all' = every status. */
export type SignalLeadStatusFilter = SignalLeadStatus | 'open' | 'low_fit' | 'all';
export type SignalLeadSort = 'recent' | 'fit';
export type SignalsMode = 'human' | 'autopilot';

export interface ISignalSettings {
  icp: { offer: string; idealBuyer: string; notAFit: string };
  icpVersion: number;
  hasIcp: boolean;
  mode: SignalsMode;
  autoApproveScore: number;
  ignoreScore: number;
}

export interface ISignalSettingsResponse {
  settings: ISignalSettings;
  /** This member's own campaign for autopilot leads they own. */
  myCampaign: { _id: string; name: string; status: string } | null;
  /** Workspace admins edit the ideal buyer, mode and scores. */
  canEdit: boolean;
}
export type LinkedInReadKind = 'profile' | 'post' | 'search' | 'relations' | 'raw' | 'analytics';

export interface ISignalsAccess {
  allowed: boolean;
  canAddSource: boolean;
  reason: 'growth' | 'trial' | 'trial_limit' | 'upgrade_required' | 'disabled' | 'no_workspace';
  isTrial: boolean;
  isGrowth: boolean;
  /** Trial only: how many sources may be on at once. */
  limit: number | null;
  used: number;
  remaining: number | null;
  requiresUpgrade: boolean;
  signalsEnabled: boolean;
  message: string | null;
}

/** A cloud (Unipile) LinkedIn account of the workspace — every teammate's, not only yours. */
export interface ISignalAccount {
  accountId: string;
  identityId: string | null;
  name: string;
  /** LinkedIn login email, when the account was connected with one. */
  email: string;
  /** Whose leads its signals become: the sender's creator, else the first campaign's. */
  ownerUserId: string | null;
  ownerName: string;
  /** Campaigns that send through it. */
  usedBy: string[];
  isMine: boolean;
  active: boolean;
  /** LinkedIn reads made through this account today, by every part of LeadDelta. */
  readsToday: Record<LinkedInReadKind, number>;
}

export interface ISignalAccountsResponse {
  accounts: ISignalAccount[];
  dailyReadCaps: Record<LinkedInReadKind, number>;
}

export interface ISignalSource {
  _id: string;
  accountId: string;
  identityId: string | null;
  ownerUserId: string | null;
  type: SignalSourceType;
  label: string;
  postUrl: string;
  enabled: boolean;
  nextRunAt: string | null;
  lastRunAt: string | null;
  lastSuccessAt: string | null;
  lastError: string;
  lastErrorAt: string | null;
  stats: { runs: number; signals: number };
  createdAt: string;
}

export interface ISignalPerson {
  providerId?: string;
  publicIdentifier?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  headline?: string;
  profilePicture?: string;
  profileUrl?: string;
  networkDistance?: string;
}

export interface ISignalLead {
  _id: string;
  personKey: string;
  person: ISignalPerson;
  ownerUserId: string | null;
  owner: { _id: string; firstName?: string; lastName?: string; profilePicture?: string } | null;
  accountId: string;
  isConnection: boolean;
  signalTypes: SignalType[];
  signalCount: number;
  firstSignalAt: string | null;
  lastSignalAt: string | null;
  lastSignal: { type: SignalType | ''; summary: string; occurredAt: string | null };
  status: SignalLeadStatus;
  statusChangedAt: string | null;
  campaignId: string | null;
  campaign: { _id: string; name: string } | null;
  fitScore: number | null;
  fitReason: string;
  fitIcpVersion: number | null;
  autoEnrolledAt: string | null;
}

export interface ISignalLeadsResponse {
  leads: ISignalLead[];
  total: number;
  page: number;
  limit: number;
  counts: Record<SignalLeadStatus | 'low_fit', number>;
}

export interface ISignal {
  _id: string;
  type: SignalType;
  person: ISignalPerson;
  context: {
    postId?: string;
    postUrl?: string;
    postExcerpt?: string;
    commentText?: string;
    reaction?: string;
    lastVisited?: string;
    newTitle?: string;
    newCompany?: string;
  };
  occurredAt: string;
}

export const SIGNAL_SOURCE_INFO: Record<Exclude<SignalSourceType, 'post'>, { title: string; description: string }> = {
  profile_views: {
    title: 'Who viewed the profile',
    description: 'Checked once a day. LinkedIn shows the full list only to Premium accounts.',
  },
  job_changes: {
    title: 'Job changes in the network',
    description: 'LinkedIn’s “Catch up” feed of this account’s connections, read once a day in the cloud. Works for accounts set to English or German.',
  },
  own_posts: {
    title: 'Engagement on recent posts',
    description: 'Comments and reactions on this account’s last few posts, checked twice a day.',
  },
};

export const SIGNAL_TYPE_LABELS: Record<SignalType, string> = {
  profile_view: 'Profile view',
  post_comment: 'Comment',
  post_reaction: 'Reaction',
  job_change: 'Job change',
};

export const SIGNAL_STATUS_LABELS: Record<SignalLeadStatus, string> = {
  new: 'New',
  approved: 'Approved',
  dismissed: 'Dismissed',
  in_campaign: 'In campaign',
};
